// Landing page - the front door, framed as an API reference/playground
// rather than a standalone tool. What secure-crypt is, the three ways to
// integrate it, and links into every other page.
import { el } from "./dom";
import type { TabId } from "../main";

interface LinkCard {
  tab: TabId;
  title: string;
  description: string;
}

const CAPABILITY_LINKS: LinkCard[] = [
  {
    tab: "keygen",
    title: "Generate a keypair",
    description: "X25519 keypair generation, passphrase-wrapped private key at rest.",
  },
  {
    tab: "encrypt-string",
    title: "Encrypt a string",
    description: "Seal a short message for one recipient's public key.",
  },
  {
    tab: "decrypt-string",
    title: "Decrypt a string",
    description: "Open a message sealed to your currently active key.",
  },
  {
    tab: "encrypt-file",
    title: "Encrypt a file",
    description: "Password-slot streaming file encryption, or seal a whole file to a recipient's public key.",
  },
  {
    tab: "decrypt-file",
    title: "Decrypt a file",
    description: "Open a file with a password, or with an envelope + your active key.",
  },
];

const OTHER_LINKS: LinkCard[] = [
  {
    tab: "integration",
    title: "Integration guide",
    description: "Using secure-crypt's encryption from your own app, without adopting this whole UI.",
  },
  {
    tab: "sdk",
    title: "SDK",
    description: "The JS/TS crypto primitives, standalone - install and run the same encryption client-side.",
  },
  {
    tab: "service-api",
    title: "Service API",
    description: "HTTP endpoints for storing ciphertext and publishing/looking up public keys, with a live try-it.",
  },
  {
    tab: "security",
    title: "Security",
    description: "What this app can and can't see, and the browser's trust-model trade-offs.",
  },
];

function renderLinkCard(link: LinkCard, onNavigate: (tab: TabId) => void): HTMLElement {
  const card = el("button", { type: "button", class: "overview-card" }, [
    el("h3", {}, [link.title]),
    el("p", {}, [link.description]),
  ]);
  card.addEventListener("click", () => onNavigate(link.tab));
  return card;
}

function pageLink(label: string, tab: TabId, onNavigate: (tab: TabId) => void): HTMLElement {
  const btn = el("button", { type: "button", class: "link-btn" }, [label]);
  btn.addEventListener("click", () => onNavigate(tab));
  return btn;
}

export function renderOverviewPanel(onNavigate: (tab: TabId) => void): HTMLElement {
  const integrationLink = pageLink("Integration guide", "integration", onNavigate);
  const sdkLink = pageLink("SDK", "sdk", onNavigate);
  const securityLink = pageLink("Security", "security", onNavigate);
  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Overview"]),
    el("h2", {}, ["Integrate secure-crypt's encryption into your own app"]),
    el("p", { class: "lede" }, [
      "Every page here documents one capability and lets you try it for real, right in the browser -- the form below each page's code snippets is a live, working demo, not a mockup.",
    ]),
    el("div", { class: "card prose" }, [
      el("p", {}, [
        "All encryption, decryption, and key derivation happen client-side. The server (",
        el("code", {}, ["service/"]),
        ") only ever relays ciphertext and public keys -- it never sees plaintext, a password, or a private key. See the ",
        securityLink,
        " page for what this app can and can't see.",
      ]),
      el("h3", {}, ["Three ways to integrate"]),
      el("p", {}, [
        el("strong", {}, ["JS/TS library (SDK)"]),
        " - install ",
        el("code", {}, ["secure-crypt-sdk"]),
        " and run the same crypto client-side in your own web app. No network call for the crypto itself.",
      ]),
      el("p", {}, [
        el("strong", {}, ["CLI"]),
        " - script encryption/decryption from a backend or shell pipeline (",
        el("code", {}, ["python -m cli.main ..."]),
        "). The \"share a file for a recipient\" flow needs no interactive input at all, so it's safe to call in a loop.",
      ]),
      el("p", {}, [
        el("strong", {}, ["HTTP relay"]),
        " - optional storage/lookup only: publish or fetch a public key, upload or download an encrypted container. See the Service API page for the full endpoint reference and a live try-it.",
      ]),
      el("p", {}, [
        "Full walkthroughs live on the ",
        integrationLink,
        " and ",
        sdkLink,
        " pages.",
      ]),
    ]),
    el("p", { class: "section-label" }, ["Capabilities"]),
    el(
      "div",
      { class: "overview-grid" },
      CAPABILITY_LINKS.map((link) => renderLinkCard(link, onNavigate)),
    ),
    el("p", { class: "section-label" }, ["Reference"]),
    el(
      "div",
      { class: "overview-grid" },
      OTHER_LINKS.map((link) => renderLinkCard(link, onNavigate)),
    ),
  ]);
}
